import { useCallback, useEffect, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { API_URL } from 'constants/index';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { CompanyContactPerson } from 'shared/types/contact';

/**
 * Contactpersonen van één firma ophalen en aanmaken.
 *
 * Voedt `CompanyContactPicker` op de plekken waar je bij een leverancier een
 * contactpersoon kiest (helpdesktickets, renovatietickets, renovatiedocumenten).
 * De lijst wordt pas geladen wanneer er een `companyId` is én `enabled` waar is,
 * zodat een dichte kiezer geen netwerkverkeer veroorzaakt.
 */
export interface NewCompanyContactInput {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  jobTitle?: string;
}

export const useCompanyContacts = (
  companyId: number | null | undefined,
  enabled = true,
) => {
  const { session } = useSession();
  const [contacts, setContacts] = useState<CompanyContactPerson[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    if (!session || !companyId) {
      setContacts([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/company/${companyId}/contacts`,
        { method: 'GET' },
        session,
      );
      if (!response.ok) {
        throw new Error(`Contactpersonen ophalen mislukt (${response.status})`);
      }
      setContacts(await response.json());
    } catch (err) {
      Sentry.captureException(err, {
        tags: { module: 'contact', action: 'fetchCompanyContacts' },
        extra: { companyId },
      });
      setError('Contactpersonen konden niet geladen worden');
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  }, [session, companyId]);

  useEffect(() => {
    if (!enabled) return;
    void fetchContacts();
  }, [enabled, fetchContacts]);

  /**
   * Nieuwe contactpersoon bij deze firma aanmaken. Geeft de aangemaakte (of al
   * bestaande — de backend ontdubbelt) persoon terug, zodat de aanroeper hem
   * meteen kan selecteren zonder de lijst opnieuw af te wachten.
   */
  const createContact = useCallback(
    async (input: NewCompanyContactInput): Promise<CompanyContactPerson> => {
      if (!session) throw new Error('Geen actieve sessie');
      if (!companyId) throw new Error('Geen firma gekozen');

      const response = await fetchWithAuth(
        `${API_URL}/contact/company/${companyId}/contacts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: input.firstName?.trim(),
            lastName: input.lastName?.trim() || undefined,
            email: input.email?.trim() || undefined,
            phone: input.phone?.trim() || undefined,
            jobTitle: input.jobTitle?.trim() || undefined,
          }),
        },
        session,
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const bericht = body?.message || 'Contactpersoon kon niet aangemaakt worden';
        const fout = new Error(bericht);
        Sentry.captureException(fout, {
          tags: { module: 'contact', action: 'createCompanyContact' },
          extra: { companyId, status: response.status, input },
        });
        throw fout;
      }

      const created: CompanyContactPerson = await response.json();
      // Lokaal toevoegen (of vervangen bij een ontdubbeling) zodat de kiezer de
      // persoon meteen toont, ook wanneer de refetch nog loopt.
      setContacts((prev) => [
        created,
        ...prev.filter((contact) => contact.id !== created.id),
      ]);
      return created;
    },
    [session, companyId],
  );

  return { contacts, isLoading, error, refetch: fetchContacts, createContact };
};
